import { transparentInverseClasses } from '@liveauctioneers/hammer-ui-core/iconButton';
import { CornersOut } from '@liveauctioneers/hammer-ui-core/icons';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

type PreviewImage = {
    alt: string;
    baseUrl: string;
};

type Props = {
    enlargeImageLabel: string;
    onEnlargeImage: () => void;
    previewImage: PreviewImage | undefined;
    tooltipId: string;
};

/**
 * Picture-in-picture (PIP) thumbnail when the main column shows expanded video — not OS/browser PiP video.
 * Same native `<button>` rationale as the gallery video PIP: predictable stacking with media and tooltips.
 */
export const BiddingConsoleExpandedGalleryPip = ({
    enlargeImageLabel,
    onEnlargeImage,
    previewImage,
    tooltipId,
}: Props) => (
    <div className="relative isolate flex max-h-[96px] min-h-[88px] flex-col items-center justify-center gap-2xs overflow-hidden rounded-xs bg-background-inverse py-2xs">
        <div className="flex max-h-[72px] w-full max-w-[171px] flex-1 items-center justify-center overflow-hidden px-2xs">
            {previewImage ? (
                <div className="flex aspect-square max-h-2xl max-w-2xl items-center justify-center overflow-hidden rounded-sm bg-background-primary">
                    <img
                        alt={previewImage.alt}
                        className="max-h-full max-w-full object-contain"
                        decoding="async"
                        height={64}
                        loading="lazy"
                        src={previewImage.baseUrl}
                        width={64}
                    />
                </div>
            ) : (
                <div
                    aria-hidden="true"
                    className="aspect-square max-h-2xl min-h-2xl w-full max-w-2xl rounded-sm bg-background-primary/20"
                />
            )}
        </div>
        <Tooltip
            className="!z-[120]"
            id={tooltipId}
            place="bottom"
        >
            {enlargeImageLabel}
        </Tooltip>
        <button
            aria-label={enlargeImageLabel}
            className={cn(
                'hui-focus-visible-outline absolute right-0 top-0 z-[100] flex items-center justify-center rounded-full',
                'h-lg min-h-lg w-lg min-w-lg',
                transparentInverseClasses,
                'consoleThreeColumn:hidden'
            )}
            data-tooltip-id={tooltipId}
            onClick={onEnlargeImage}
            type="button"
        >
            <CornersOut
                $withoutAriaLabel
                color="inherit"
                size="sm"
            />
        </button>
    </div>
);
